import { Component } from '@theme/component';

/**
 * Accessible tab switcher used by the "Tabbed product grid" section.
 * Panels are server-rendered; switching only toggles visibility.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [tablist]
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 *
 * @extends Component<Refs>
 */
class ClaspLabTabs extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
    this.#collectTabs();
  }

  /**
   * Theme blocks render their own tab button next to their panel. Move every button into the
   * shared tab bar (keeping DOM order) and make sure exactly one tab is selected.
   */
  #collectTabs() {
    const { tablist } = this.refs;
    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    if (tablist instanceof HTMLElement) {
      for (const tab of tabs) {
        if (tab.parentElement !== tablist) tablist.append(tab);
      }
    }
    const selected = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.select(selected === -1 ? 0 : selected);
    if (tablist instanceof HTMLElement && tabs.length < 2) tablist.hidden = true;
  }

  /**
   * Click handler for a tab button: `on:click="/selectTab"`.
   * @param {Event} event
   */
  selectTab(event) {
    const target = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    const index = tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (index !== -1) this.select(index);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * Selects a tab by index. Wired declaratively via `on:click="/select/{index}"`.
   * @param {string | number} index
   */
  select(index) {
    const target = Number(index);
    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    const panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    tabs.forEach((tab, i) => {
      const selected = i === target;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      panel.hidden = i !== target;
    });
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    let next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    this.select(next);
    tabs[next]?.focus();
  };
}

if (!customElements.get('cl-tabs-component')) customElements.define('cl-tabs-component', ClaspLabTabs);
